import { File, Loader2, Upload, X } from "lucide-react";
import type React from "react";
import { useFileUpload } from "@/features/sources/hooks/useFileUpload";
import { formatFileSize } from "@/utils/fileValidation";

interface AddSourcesModalProps {
	isOpen: boolean;
	onClose: () => void;
}

const AddSourcesModal: React.FC<AddSourcesModalProps> = ({ isOpen, onClose }) => {
	const {
		selectedFile,
		validationResult,
		isDragging,
		fileInputRef,
		uploadMutation,
		reset,
		handleFileInputChange,
		handleDrop,
		handleDragOver,
		handleDragLeave,
		handleBrowseClick,
		handleUpload,
	} = useFileUpload({ onSuccess: onClose });

	const handleClose = () => {
		reset();
		onClose();
	};

	if (!isOpen) return null;

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
			<div className="surface rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
				{/* Header */}
				<div className="sticky top-0 surface border-b border-subtle px-6 py-4 flex items-center justify-between rounded-t-xl">
					<h2 className="text-xl font-semibold text-primary">Add sources</h2>
					<div className="flex items-center gap-3">
						<button
							type="button"
							onClick={handleClose}
							className="p-2 soft-hover rounded-lg transition-colors"
							aria-label="Close"
						>
							<X className="w-5 h-5 text-muted" />
						</button>
					</div>
				</div>

				{/* Content */}
				<div className="p-6">
					{/* Description */}
					<p className="text-sm text-muted mb-6">
						Sources let the system base its responses on the information that matters most to you. (Examples: marketing
						plans, course reading, research notes, meeting transcripts, sales documents, etc.)
					</p>

					{/* Upload Area */}
					{/* biome-ignore lint/a11y/useSemanticElements: Drag-and-drop area requires div with role */}
					<div
						role="button"
						tabIndex={0}
						aria-label="Drag and drop area for file upload"
						className={`border-2 border-dashed rounded-xl p-12 text-center transition-all duration-200 ${
							isDragging
								? "border-primary-500 bg-primary-500/10 scale-[1.02]"
								: validationResult && !validationResult.valid
									? "border-red-500 bg-red-500/10"
									: selectedFile
										? "border-green-500 bg-green-500/10"
										: "border-strong surface-alt soft-hover"
						}`}
						onDrop={handleDrop}
						onDragOver={handleDragOver}
						onDragLeave={handleDragLeave}
					>
						{selectedFile ? (
							<div className="space-y-4">
								<div className="flex items-center justify-center">
									<div className="rounded-full p-3 border border-green-500/30 bg-green-500/12">
										<File className="w-8 h-8 text-green-500" />
									</div>
								</div>
								<div>
									<div className="flex items-center justify-center gap-2 mb-2">
										<span className="font-medium text-primary">{selectedFile.name}</span>
										<button
											type="button"
											onClick={reset}
											className="ml-2 p-1 soft-hover rounded-full transition-colors"
											aria-label="Remove file"
										>
											<X className="w-4 h-4 text-subtle" />
										</button>
									</div>
									<p className="text-sm text-subtle">{formatFileSize(selectedFile.size)}</p>
								</div>
							</div>
						) : (
							<div className="space-y-4">
								<div className="flex justify-center">
									<div className="surface-alt rounded-full p-4">
										<Upload className="w-8 h-8 text-muted" />
									</div>
								</div>
								<div>
									<h3 className="text-lg font-semibold text-primary mb-2">Upload sources</h3>
									<p className="text-sm text-muted mb-4">
										Drag & drop or{" "}
										<button
											type="button"
											onClick={handleBrowseClick}
											className="text-primary-600 hover:text-primary-700 underline"
										>
											choose file
										</button>{" "}
										to upload
									</p>
								</div>
							</div>
						)}
					</div>

					<input
						ref={fileInputRef}
						type="file"
						className="hidden"
						accept=".pdf,.docx,.txt,.rtf,.md"
						onChange={handleFileInputChange}
					/>

					{/* Supported File Types */}
					<p className="text-xs text-subtle mt-4 text-center">PDF, .txt, Markdown, .docx</p>

					{/* Validation Errors */}
					{validationResult && !validationResult.valid && (
						<div className="mt-4 p-4 rounded-lg border border-red-500/35 bg-red-500/12">
							<div className="flex items-start space-x-2">
								<div className="flex-1">
									<h4 className="font-medium text-red-600 dark:text-red-300 mb-1">File Validation Errors</h4>
									<ul className="list-disc list-inside text-sm text-red-600 dark:text-red-200 space-y-1">
										{validationResult.errors.map((error) => (
											<li key={error}>{error}</li>
										))}
									</ul>
								</div>
							</div>
						</div>
					)}

					{/* Upload Button */}
					{selectedFile && validationResult?.valid && (
						<div className="mt-6 flex items-center justify-end">
							<button
								type="button"
								onClick={handleUpload}
								disabled={uploadMutation.isPending}
								className="btn btn-primary px-6 py-2.5 flex items-center gap-2"
							>
								{uploadMutation.isPending ? (
									<>
										<Loader2 className="w-5 h-5 animate-spin" />
										<span>Uploading...</span>
									</>
								) : (
									<>
										<Upload className="w-5 h-5" />
										<span>Upload</span>
									</>
								)}
							</button>
						</div>
					)}

					{/* Upload Error */}
					{uploadMutation.isError && (
						<div className="mt-4 p-4 rounded-lg border border-red-500/35 bg-red-500/12">
							<div className="flex items-start space-x-2">
								<div className="flex-1">
									<h4 className="font-medium text-red-600 dark:text-red-300 mb-1">Upload Failed</h4>
									<p className="text-sm text-red-600 dark:text-red-200">
										{uploadMutation.error instanceof Error
											? uploadMutation.error.message
											: "An error occurred during upload. Please try again."}
									</p>
								</div>
							</div>
						</div>
					)}
				</div>

				{/* Footer Disclaimer */}
				<div className="sticky bottom-0 surface-alt border-t border-subtle px-6 py-3 rounded-b-xl">
					<p className="text-xs text-subtle text-center">
						The system can be inaccurate; please double check its responses.
					</p>
				</div>
			</div>
		</div>
	);
};

export default AddSourcesModal;
